import type { OrderOptionDto } from '../dto/order';
import { resolveOrderDeliveryAddress } from '../utils/orderDeliveryAddress';
import type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  TransportRouteDetailDto,
} from '../dto/transport';
import {
  buildTransportFormValues,
  buildTransportRequestPayload,
  type TransportFormValues,
} from './transportFormUtils';
import {
  type AllocationMethod as TransportCostAllocationMethod,
  BETWEEN_LOADS_METHOD,
  DEFAULT_ALLOCATION_METHOD,
  TRAILER_PAYLOAD_KG,
  allocateProportionally,
  calculateProductAreaSqm,
  calculateProductPieces,
  calculateProductVolumeCbm,
  hasMixedUnits,
  positionArea,
  positionVolume,
  type AllocationKeyPosition,
} from '../utils/transportCostAllocation';
import {
  calculateAreaM2,
  calculateVolumeM3,
  normalizeQuantityUnit,
  type ConversionSource,
} from '../utils/dimensionConversion';
import { formatUnit } from '../utils/units';
import {
  deriveTransportDirection,
  type TransportDirection,
} from '../modules/transports/transportDirection';
import { getEffectiveTransportReservationQuantity } from '../utils/transportCargoAllocation';
import { quantitySetToFields, resolveStoredQuantitySet } from '../utils/commercialQuantity';

// Single home for the form's default transport-cost currency. The create-mode
// default below and every in-form display fallback (preview column, manual
// InputNumber addons, sticky summary) must read this constant rather than
// hardcoding 'USD' or 'PLN' independently, so a future default change is a
// one-line edit. NOT used for the PLN-canonical amounts stored in
// TransportOrderAllocation (see TransportRouteDetail.tsx) — those are always
// literal 'PLN' regardless of this constant.
export const DEFAULT_TRANSPORT_CURRENCY = 'PLN';

/**
 * Map of `orderProductId -> OrderProductRealizationDto` aggregated across all
 * selected orders. Lets the cargo table show "already on X other transports:
 * 120 m² (remaining: 80 m²)" so the user can split one order across multiple
 * trucks without double-allocating.
 */
export type AllocationHintMap = Record<string, OrderProductRealizationDto>;

/**
 * Merge the per-order realization responses into a single hint map keyed by
 * orderProductId. When the form is editing an existing transport, the cargo
 * currently on THIS transport is subtracted from the map so the hint reflects
 * OTHER transports only — otherwise the user would see their own load twice.
 *
 * Kept here (not inline in SharedTransportForm) so the "subtract own cargo"
 * invariant lives in one place and can be unit-tested independently.
 */
export function computeAllocationHints(
  perOrderRealizations: OrderTransportsDto[],
  currentTransport?: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders' | 'status'> | null,
): AllocationHintMap {
  const ownCargoByProductId: Record<string, number> = {};
  // The server realization endpoint already excludes CANCELLED transports.
  // Subtracting this truck again would hide cargo reserved by other active
  // trucks and show a false remaining allowance during cancelled-truck edits.
  const subtractOwnCargo = currentTransport?.status !== 'CANCELLED';
  const ownCargoItems = currentTransport?.cargoItems || [];
  const orderIdsWithOwnCargo = new Set<string>();

  if (subtractOwnCargo) {
    ownCargoItems.forEach((item) => {
      if (item.orderId) {
        orderIdsWithOwnCargo.add(item.orderId);
      }

      if (item.orderProductId) {
        ownCargoByProductId[item.orderProductId] =
          (ownCargoByProductId[item.orderProductId] || 0)
          + getEffectiveTransportReservationQuantity({
            status: currentTransport?.status,
            quantity: Number(item.quantity || 0),
            deliveredQuantity: item.deliveredQuantity,
          });
      }
    });
  }

  if (subtractOwnCargo) {
    (currentTransport?.routeOrders || []).forEach((routeOrder: RouteOrderDto) => {
      if (!routeOrder.orderId || orderIdsWithOwnCargo.has(routeOrder.orderId)) {
        return;
      }

      (routeOrder.order?.products || []).forEach((product) => {
        if (!product.id) {
          return;
        }

        ownCargoByProductId[product.id] = Math.max(
          ownCargoByProductId[product.id] || 0,
          Number(product.quantity || 0),
        );
      });
    });
  }

  const merged: AllocationHintMap = {};
  perOrderRealizations.forEach((result) => {
    result.productRealization.forEach((realization) => {
      const ownPortion = ownCargoByProductId[realization.orderProductId] || 0;
      const adjustedAllocated = Math.max(realization.allocatedQuantity - ownPortion, 0);
      merged[realization.orderProductId] = {
        ...realization,
        allocatedQuantity: adjustedAllocated,
        remainingQuantity: Math.max(realization.orderedQuantity - adjustedAllocated, 0),
      };
    });
  });
  return merged;
}

// Distinct, order-preserving list of the delivery/unload places carried by the
// selected orders. Used to prefill the delivery-address blocks in create mode.
export function deriveDeliveryAddressesFromOrders(orders: OrderOptionDto[]): string[] {
  const seen = new Set<string>();
  const addresses: string[] = [];

  orders.forEach((order) => {
    // Order „Adres dostawy”, else the customer card address.
    const place = resolveOrderDeliveryAddress(order);
    if (place && !seen.has(place)) {
      seen.add(place);
      addresses.push(place);
    }
  });

  return addresses;
}

// Distinct, order-preserving list of the factory/loading places carried by the
// selected orders. Used to prefill the loading-address blocks in create mode.
export function deriveLoadingAddressesFromOrders(orders: OrderOptionDto[]): string[] {
  const seen = new Set<string>();
  const addresses: string[] = [];

  orders.forEach((order) => {
    const place = String(order.factoryLocation || '').trim();
    if (place && !seen.has(place)) {
      seen.add(place);
      addresses.push(place);
    }
  });

  return addresses;
}

// Deduped, comma-joined customer names of the selected orders. Auto-fills the
// carrier-section customer field so it is never a dead manual field.
export function deriveCustomerFromOrders(orders: OrderOptionDto[]): string {
  const seen = new Set<string>();
  const names: string[] = [];

  orders.forEach((order) => {
    const name = String(order.customerName || '').trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  });

  return names.join(', ');
}


export interface SharedTransportOrderMetrics {
  weight: number;
  areaSqm: number;
  volume: number;
  // Mirrors backend transportMetrics areaIncomplete / volumeIncomplete: ≥1
  // loaded line could not derive the metric (no product dimensions), so an
  // SQM/CBM split understates this order — never guessed.
  areaIncomplete: boolean;
  volumeIncomplete: boolean;
  /** ≥1 loaded product is counted only in pieces (no m² / m³, not "missing"). */
  countedInPiecesOnly: boolean;
  /** ≥1 loaded line has no piece weight (own or catalog): `weight` is a lower bound, never „0 = nothing”. */
  weightIncomplete?: boolean;
  quantityByUnit: Record<string, number>;
}

export interface SharedTransportPreviewRow {
  orderId: string;
  displayOrderNumber: string;
  customerName: string;
  /** `null` = not known on this screen (goods value: server-side only). */
  basisValue: number | null;
  allocationPercent: number | null;
  allocatedTransportCost: number | null;
  metrics: SharedTransportOrderMetrics;
}

export interface SharedTransportCargoItem {
  id?: string;
  orderId: string;
  orderProductId?: string;
  productName: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  deliveredQuantity?: number | null;
  deliveredQuantityPcs?: number | null;
  deliveredQuantityM2?: number | null;
  deliveredQuantityM3?: number | null;
  deliveryConfirmedAt?: string | null;
  deliveryConfirmedById?: string | null;
  unit?: string;
  commercialQuantity?: number | null;
  commercialUnit?: string | null;
  goodsValueTotal?: number | null;
  manualAllocatedTransportCost?: number | null;
  grossWeight?: number;
  /** Catalog piece weight (kg): display fallback only, never sent — the backend
   *  applies the same fallback when it saves, so an unchanged old truck is not resent. */
  catalogGrossWeight?: number;
  width?: number;
  height?: number;
  length?: number;
  thickness?: number;
  /** „Liczymy tylko w sztukach” of the product (migration 196). */
  countInPiecesOnly?: boolean;
}

export type SharedTransportFormValues = TransportFormValues & {
  /** WP14: who pays this truck — INBOUND (partie ZD) or OUTBOUND (ZK). */
  direction?: TransportDirection;
  costAllocationMethod?: TransportCostAllocationMethod;
  [key: string]: any;
};

export function buildSharedTransportFormValues(
  transport?: TransportRouteDetailDto | null,
): SharedTransportFormValues {
  if (!transport) {
    return {
      // DB default is PLN; the create form mirrors that so a saved-as-is
      // transport keeps a consistent currency. Edit mode keeps the stored value.
      currency: DEFAULT_TRANSPORT_CURRENCY,
      status: 'SCHEDULED',
      customer: '',
      transportCompany: '',
      transportCompanyId: null,
      loadingAddresses: [''],
      deliveryAddresses: [''],
      loadingDate: null,
      estimatedDelivery: null,
      actualDelivery: null,
      transportRate: null,
      totalWeight: null,
      totalVolume: null,
      // One key per truck, default szt. (decision 2026-09-30).
      costAllocationMethod: DEFAULT_ALLOCATION_METHOD,
      // WP14: a new truck starts OUTBOUND; the form follows the selected
      // orders (a ZD on board -> INBOUND) until the operator picks one.
      direction: deriveTransportDirection(0),
    };
  }

  const firstCustomerName = transport.routeOrders[0]?.order?.customerName || transport.customerName || '';

  return {
    ...buildTransportFormValues(transport, {
      customer: { name: firstCustomerName },
      currency: transport.currency,
      factoryLocation: transport.loadingAddresses?.[0] || '',
      deliveryLocation: transport.deliveryAddresses?.[0] || '',
    }),
    orderRoutePointsByOrder: Object.fromEntries(transport.routeOrders.map(row => [row.order.id || row.orderId, { loadingAddress: row.loadingAddress ?? null, deliveryAddress: row.deliveryAddress ?? null }])),
    customer: firstCustomerName,
    transportCompanyId: transport.transportCompanyId ?? null,
    costAllocationMethod: transport.costAllocationMethod || DEFAULT_ALLOCATION_METHOD,
    direction: transport.direction || deriveTransportDirection(transport.supplierOrders?.length || 0),
  };
}

/** One explicit "this truck carries N of the ZD line" row (Addendum A). */
export interface SupplierCargoItemPayload {
  supplierOrderItemId: string;
  quantity: number;
  unit: 'pcs' | 'm2' | 'm3';
}

export function buildSharedTransportPayload(
  values: SharedTransportFormValues,
  orderIds: string[],
  cargoItems: SharedTransportCargoItem[] = [],
  supplierOrderIds: string[] = [],
  // Addendum A: `undefined` = the operator did not touch the ZD quantities, so
  // the key is omitted and the server keeps the stored rows; an array
  // (including []) replaces this truck's rows.
  supplierCargoItems?: SupplierCargoItemPayload[],
  // PR3 (decision 2026-10-06): ZK line → ZD line pairs by `orderProductId`,
  // only on a „Fabryka → klient” truck. `undefined` = the key is not sent on
  // any cargo row (the server keeps / clears the stored pair by itself);
  // a map sends every row's pair (null = no pair).
  cargoPairs?: ReadonlyMap<string, string | null>,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...buildTransportRequestPayload(values),
    costAllocationMethod: values.costAllocationMethod || DEFAULT_ALLOCATION_METHOD,
    direction: values.direction || deriveTransportDirection(supplierOrderIds.length),
    // The catalog flag is read-only context for the metrics, not cargo data.
    cargoItems: cargoItems.map(({ countInPiecesOnly: _catalogFlag, catalogGrossWeight: _catalogWeight, ...item }) => (cargoPairs
      ? { ...item, supplierOrderItemId: cargoPairs.get(item.orderProductId || '') ?? null }
      : item)),
  };
  // Retired fields (decision 2026-09-30): the server ignores them; never send.
  delete payload.allocationMethod;
  delete payload.packages;
  if (supplierCargoItems !== undefined) {
    payload.supplierCargoItems = supplierCargoItems;
  }

  // Etap 2 (transport przywozowy): BOTH allocation lists are omitted rather
  // than sent empty. `orderIds` is validated as a non-empty array when present
  // on create AND update ("must be a non-empty array when provided"), so an
  // inbound-only transport — zero customer orders, one or more supplier orders
  // — used to be rejected with a 400 before it ever reached the domain layer.
  // On update, an omitted `orderIds` means "keep the current customer orders"
  // (length-based contract, unlike supplierOrderIds); removing the last
  // customer order is a separate operation (unlinkOrderFromTransport), not an
  // empty array here.
  if (orderIds.length > 0) {
    payload.orderIds = orderIds;
  }

  // Etap 2 (transport przywozowy): POST still validates `supplierOrderIds` as a
  // NON-EMPTY array when present, so "no inbound leg" is expressed by omitting
  // the field, never by sending []. The UPDATE path is presence-based (absent =
  // keep, [] = clear) and is handled one level up, in
  // omitUnchangedSharedTransportAllocationFields, which is the only place that
  // knows what the transport was loaded with and can therefore tell an
  // intentional clear apart from "there was never anything here".
  if (supplierOrderIds.length > 0) {
    payload.supplierOrderIds = supplierOrderIds;
  }

  if (values.orderRoutePointsByOrder) {
    payload.orderRoutePoints = orderIds.map(orderId => ({ orderId,
      loadingAddress: values.orderRoutePointsByOrder[orderId]?.loadingAddress ?? null,
      deliveryAddress: values.orderRoutePointsByOrder[orderId]?.deliveryAddress ?? null,
    }));
  }
  delete payload.cargoItemsByKey;
  return payload;
}

function orderedIds(values: unknown): string[] {
  return Array.isArray(values)
    ? Array.from(new Set(values.map((value) => String(value || '')).filter(Boolean)))
    : [];
}

function sortedIds(values: unknown): string[] {
  return Array.isArray(values)
    ? values.map((value) => String(value || '')).filter(Boolean).sort()
    : [];
}

function normalizeCargoItemsForCompare(
  values: unknown,
  withPairing = false,
): Array<Record<string, string | number | null>> {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((item) => ({
    // PR3: the ZD pair counts only when the form sends it (factory → customer).
    ...(withPairing ? { supplierOrderItemId: item.supplierOrderItemId || null } : {}),
    orderId: item.orderId || null,
    orderProductId: item.orderProductId || null,
    // The product name is a server-owned snapshot, not an editable cargo
    // field.  The edit form deliberately renders the canonical catalog name,
    // while older cargo rows may still contain the historical order-line name.
    // Comparing that presentation value made an unrelated transport edit
    // resend `cargoItems`, and the backend consequently replaced identical
    // rows (including their IDs).  Identity, quantities and logistics fields
    // below are the persisted cargo contract; the display name is not.
    quantity: toFiniteNumber(item.quantity) || 0,
    unit: normalizeProductUnit(item.unit),
    quantityPcs: toFiniteNumber(item.quantityPcs),
    quantityM2: toFiniteNumber(item.quantityM2),
    quantityM3: toFiniteNumber(item.quantityM3),
    goodsValueTotal: toFiniteNumber(item.goodsValueTotal) || 0,
    manualAllocatedTransportCost: toFiniteNumber(item.manualAllocatedTransportCost) || 0,
    grossWeight: toFiniteNumber(item.grossWeight) || 0,
    width: toFiniteNumber(item.width) || 0,
    height: toFiniteNumber(item.height) || 0,
    length: toFiniteNumber(item.length) || 0,
    thickness: toFiniteNumber(item.thickness) || 0,
  })).sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)));
}

function normalizeDeliveryItemsForCompare(values: unknown): Array<{
  orderProductId: string;
  deliveredQuantity: number;
  notOnTruck: boolean;
}> {
  if (!Array.isArray(values)) return [];

  // „Nie jechało tym autem” changes the plan, so it is a change even with the
  // same delivered quantity (a saved delivery is always „Nie dojechało”).
  return values
    .filter((item) => item?.orderProductId)
    .map((item) => ({
      orderProductId: String(item.orderProductId),
      deliveredQuantity: toFiniteNumber(item.deliveredQuantity) ?? 0,
      notOnTruck: item.shortfall === 'NOT_ON_TRUCK',
    }))
    .sort((left, right) => left.orderProductId.localeCompare(right.orderProductId));
}

export function getEffectiveTransportCargoItems(transport: TransportRouteDetailDto | null | undefined): SharedTransportCargoItem[] {
  if (!transport) {
    return [];
  }

  if (transport.cargoItems.length > 0) {
    return transport.cargoItems;
  }

  return transport.routeOrders.flatMap((routeOrder) => (
    (routeOrder.order.products || []).map((product) => {
      const quantitySet = resolveStoredQuantitySet(product as unknown as Record<string, unknown>);
      return {
        orderId: routeOrder.orderId,
        orderProductId: product.id,
        productName: product.name,
        quantity: product.quantity ?? 0,
        ...quantitySetToFields(quantitySet),
        unit: product.unit,
        goodsValueTotal: 0,
        manualAllocatedTransportCost: null,
        grossWeight: product.grossWeight,
        width: product.width,
        height: product.height,
        length: product.length,
        thickness: product.thickness,
        ...(product.countInPiecesOnly === true ? { countInPiecesOnly: true } : {}),
      };
    })
  ));
}

// HOTEL (HIGH, "warn, never block"): a cargo line's PERSISTED loaded quantity
// can end up exceeding the order product's CURRENT quantity when the order is
// edited down after the transport was already built (e.g. order 120 -> 100
// szt, transport still shows/loads 120 with zero indication). This is a
// non-blocking, informational detection ONLY — no auto-resync, no save
// blocking (the existing per-line overflow guard in
// TransportOrdersCargoSection, which compares against allocatedElsewhere
// across ALL transports, stays exactly as-is and is a separate concern).
//
// Single home so TransportRouteDetail.tsx (read-only) and the edit form
// (SharedTransportForm/TransportOrdersCargoSection) render the identical
// warning from the identical detection, rather than reimplementing the
// order-product lookup twice. Both surfaces already have everything this
// needs in their existing DTOs — routeOrders[].order.products[].quantity is a
// LIVE `SELECT` against "OrderProduct" (transportRepository.js), never a
// cargo snapshot, so no backend/DTO change was required.
export interface StaleCargoQuantityWarning {
  orderId: string;
  orderProductId: string;
  displayOrderNumber: string;
  productName: string;
  loadedQuantity: number;
  currentQuantity: number;
  unit?: string;
}

export function findStaleCargoQuantityWarnings(
  transport: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders'> | null | undefined,
): StaleCargoQuantityWarning[] {
  if (!transport) {
    return [];
  }

  // Map of orderProductId -> { current OrderProduct quantity, owning order's
  // display number }. Built once across every linked order so a cargo item is
  // matched regardless of which order it belongs to.
  const currentProductsById = new Map<string, { quantity: number; displayOrderNumber: string; orderId: string }>();
  // S25 (CRITICAL, defense in depth): orderId -> productName -> orderProductId,
  // for the same NULL-orderProductId fallback the backend allocation guard
  // uses (getTransportsForOrder/transportService.js) — an orphaned cargo row
  // (ON DELETE SET NULL) must still be checked against the CURRENT quantity
  // instead of going blind, which is exactly the scenario this banner exists
  // to catch.
  const productIdByOrderAndName = new Map<string, string>();
  (transport.routeOrders || []).forEach((routeOrder: RouteOrderDto) => {
    const displayOrderNumber = routeOrder.order?.displayOrderNumber || routeOrder.order?.orderNumber || '';
    (routeOrder.order?.products || []).forEach((product) => {
      if (!product.id) {
        return;
      }
      currentProductsById.set(product.id, {
        quantity: toFiniteNumber(product.quantity) ?? 0,
        displayOrderNumber,
        orderId: routeOrder.orderId,
      });
      const nameKey = `${routeOrder.orderId}::${product.name || ''}`;
      if (product.name && !productIdByOrderAndName.has(nameKey)) {
        productIdByOrderAndName.set(nameKey, product.id);
      }
    });
  });

  const warnings: StaleCargoQuantityWarning[] = [];
  (transport.cargoItems || []).forEach((cargoItem) => {
    const fallbackOrderId = cargoItem.orderId;
    const fallbackNameKey = fallbackOrderId ? `${fallbackOrderId}::${cargoItem.productName || ''}` : null;
    const resolvedOrderProductId = cargoItem.orderProductId
      || (fallbackNameKey ? productIdByOrderAndName.get(fallbackNameKey) : undefined);

    if (!resolvedOrderProductId) {
      // No FK to compare against and no name-based fallback match (legacy row,
      // or the line was fully removed from the order, not just reduced) —
      // nothing reliable to detect.
      return;
    }

    const currentProduct = currentProductsById.get(resolvedOrderProductId);
    if (!currentProduct) {
      // The order product this cargo line pointed to no longer exists on the
      // order at all (fully removed, not just reduced) — a different failure
      // mode than HOTEL's "quantity reduced" repro; not surfaced here.
      return;
    }

    const loadedQuantity = toFiniteNumber(cargoItem.quantity) ?? 0;
    if (loadedQuantity > currentProduct.quantity) {
      warnings.push({
        orderId: currentProduct.orderId,
        orderProductId: resolvedOrderProductId,
        displayOrderNumber: currentProduct.displayOrderNumber,
        productName: cargoItem.productName,
        loadedQuantity,
        currentQuantity: currentProduct.quantity,
        unit: cargoItem.unit,
      });
    }
  });

  return warnings;
}

// F3 (order coverage on transport surfaces, small): "Załadowano X z Y szt." —
// how much of an order's total quantity is loaded on THIS transport, vs the
// order's current total. Reuses the SAME data findStaleCargoQuantityWarnings
// uses (routeOrders[].order.products for the current order quantity,
// transport.cargoItems for what is loaded here) — synergy intended, no
// separate lookup. Summed per unit (mirrors formatCargoQuantitySummary /
// formatCargoQuantityTotals) since an order can carry mixed units (pcs/m²/m³).
export interface OrderCoverageByUnit {
  unit: string;
  loadedQuantity: number;
  orderedQuantity: number;
}

export function deriveOrderCoverage(
  transport: Pick<TransportRouteDetailDto, 'cargoItems' | 'routeOrders'> | null | undefined,
  orderId: string,
): OrderCoverageByUnit[] {
  if (!transport) {
    return [];
  }

  const routeOrder = (transport.routeOrders || []).find((entry: RouteOrderDto) => entry.orderId === orderId);
  if (!routeOrder) {
    return [];
  }

  const orderedByUnit = new Map<string, number>();
  (routeOrder.order?.products || []).forEach((product) => {
    const unit = product.unit || 'pcs';
    const quantity = toFiniteNumber(product.quantity) ?? 0;
    orderedByUnit.set(unit, (orderedByUnit.get(unit) || 0) + quantity);
  });

  const loadedByUnit = new Map<string, number>();
  getEffectiveTransportCargoItems(transport as TransportRouteDetailDto)
    .filter((item) => item.orderId === orderId)
    .forEach((item) => {
      const unit = item.unit || 'pcs';
      const quantity = toFiniteNumber(item.quantity) ?? 0;
      loadedByUnit.set(unit, (loadedByUnit.get(unit) || 0) + quantity);
    });

  const units = new Set([...Array.from(orderedByUnit.keys()), ...Array.from(loadedByUnit.keys())]);
  return Array.from(units).map((unit) => ({
    unit,
    loadedQuantity: loadedByUnit.get(unit) || 0,
    orderedQuantity: orderedByUnit.get(unit) || 0,
  }));
}

// Single home for the per-line description text of a stale-quantity warning,
// so TransportRouteDetail.tsx and the edit form's Alert render byte-identical
// wording — the multi-order variant prefixes the display order number
// (`transport:cargo.staleQuantityWarningItemMultiOrder`) only when more than
// one distinct order is affected, matching the "use display order number when
// multiple orders" requirement without cluttering a single-order transport's
// warning with a redundant order number already shown elsewhere on the page.
// Both keys live under the `cargo` namespace object in transport.json (both
// locales) — S31 MED regression (2026-07-07 external run): the key strings
// here were missing that `cargo.` segment, so the banner rendered the raw
// i18n key instead of Polish text. See the JSON-path-agreement test below.
export function formatStaleCargoQuantityWarningItems(
  warnings: StaleCargoQuantityWarning[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string[] {
  const distinctOrderIds = new Set(warnings.map((warning) => warning.orderId));
  const isMultiOrder = distinctOrderIds.size > 1;

  return warnings.map((warning) => t(
    isMultiOrder
      ? 'transport:cargo.staleQuantityWarningItemMultiOrder'
      : 'transport:cargo.staleQuantityWarningItemSingleOrder',
    {
      orderNumber: warning.displayOrderNumber,
      product: warning.productName,
      loaded: warning.loadedQuantity,
      current: warning.currentQuantity,
      unit: formatUnit(warning.unit),
    },
  ));
}

/**
 * Review 2026-10-12: the update cannot say „no customer orders any more” —
 * `orderIds` must be non-empty when sent and an omitted list keeps the saved
 * ZK (see buildSharedTransportPayload). So taking the LAST ZK off a saved
 * truck goes through the canonical unlink (DELETE
 * /transport-routes/:id/orders/:orderId, EditTransportRoute) before the PUT.
 * That is safe only while a saved ZD stays on the truck (the server keeps a
 * ZD-only truck, but deletes one left with no order at all): otherwise the
 * save is `blocked` and the form asks to unlink the ZK first.
 */
export function planSavedCustomerOrderUnlink(
  payload: Record<string, unknown>,
  transport: Pick<TransportRouteDetailDto, 'routeOrders' | 'supplierOrders'>,
): { unlinkOrderIds: string[]; blocked: boolean } {
  const savedOrderIds = Array.from(new Set((transport.routeOrders || []).map((routeOrder) => routeOrder.orderId)));
  const requestedOrderIds = Array.isArray(payload.orderIds) ? payload.orderIds : [];
  if (savedOrderIds.length === 0 || requestedOrderIds.length > 0) return { unlinkOrderIds: [], blocked: false };
  const requestedSupplierOrderIds = Array.isArray(payload.supplierOrderIds) ? payload.supplierOrderIds : [];
  const savedSupplierOrderStays = (transport.supplierOrders || [])
    .some((supplierOrder) => requestedSupplierOrderIds.includes(supplierOrder.id));
  return savedSupplierOrderStays
    ? { unlinkOrderIds: savedOrderIds, blocked: false }
    : { unlinkOrderIds: [], blocked: true };
}

export function omitUnchangedSharedTransportAllocationFields(
  payload: Record<string, unknown>,
  transport: TransportRouteDetailDto,
): Record<string, unknown> {
  const nextPayload = { ...payload };
  // In order, not sorted: the ZK order is the unloading order (B1, B2, …)
  // the server saves, so a moved stop alone is a change to send.
  const currentOrderIds = orderedIds(transport.routeOrders.map((routeOrder) => routeOrder.orderId));
  const requestedOrderIds = orderedIds(nextPayload.orderIds);
  const currentCostAllocationMethod = transport.costAllocationMethod || DEFAULT_ALLOCATION_METHOD;

  // `requestedOrderIds` is [] both when the list is unchanged-and-empty and
  // when buildSharedTransportPayload omitted it; either way there is nothing to
  // delete, so this stays a plain equality check.
  if (JSON.stringify(currentOrderIds) === JSON.stringify(requestedOrderIds)) {
    delete nextPayload.orderIds;
  }

  // Etap 2 (transport przywozowy): the update contract for the inbound links is
  // PRESENCE-based — an absent `supplierOrderIds` means "keep what is there",
  // an explicit [] means "clear them all". So this has to say one of three
  // things, and the loaded transport is the only way to tell them apart:
  //
  //   unchanged            -> omit (same "don't restate an unchanged
  //                           allocation" rule as orderIds/cargoItems above, so
  //                           an edit to the driver's phone number sends no
  //                           allocation field at all)
  //   changed to a new set -> send that set (buildSharedTransportPayload
  //                           already put it there)
  //   changed to nothing   -> send [] EXPLICITLY, because
  //                           buildSharedTransportPayload omits empty lists to
  //                           stay valid on create, and an omitted field here
  //                           would silently mean "keep them" — the operator's
  //                           unlink would vanish without a trace.
  const currentSupplierOrderIds = sortedIds(
    (transport.supplierOrders || []).map((supplierOrder) => supplierOrder.id),
  );
  const requestedSupplierOrderIds = sortedIds(nextPayload.supplierOrderIds);

  if (JSON.stringify(currentSupplierOrderIds) === JSON.stringify(requestedSupplierOrderIds)) {
    delete nextPayload.supplierOrderIds;
  } else if (requestedSupplierOrderIds.length === 0) {
    nextPayload.supplierOrderIds = [];
  }

  if (nextPayload.costAllocationMethod === currentCostAllocationMethod) {
    delete nextPayload.costAllocationMethod;
  }

  const withPairing = Array.isArray(nextPayload.cargoItems)
    && nextPayload.cargoItems.some((item) => item && Object.prototype.hasOwnProperty.call(item, 'supplierOrderItemId'));
  const cargoUnchanged = (
    JSON.stringify(normalizeCargoItemsForCompare(nextPayload.cargoItems, withPairing))
    === JSON.stringify(normalizeCargoItemsForCompare(getEffectiveTransportCargoItems(transport), withPairing))
  );
  if (cargoUnchanged) {
    delete nextPayload.cargoItems;
  }

  const currentDeliveryItems = transport.status === 'DELIVERED'
    ? (transport.cargoItems || []).map((item) => ({
        orderProductId: item.orderProductId,
        deliveredQuantity: item.deliveredQuantity,
      }))
    : [];
  const deliveryUnchanged = (
    JSON.stringify(normalizeDeliveryItemsForCompare(nextPayload.deliveryItems))
    === JSON.stringify(normalizeDeliveryItemsForCompare(currentDeliveryItems))
  );
  if (cargoUnchanged && nextPayload.status === transport.status && deliveryUnchanged) {
    delete nextPayload.deliveryItems;
  }

  return nextPayload;
}

export function getCargoQuantityUnits(cargoItems: SharedTransportCargoItem[]): string[] {
  const units = new Set<string>();

  cargoItems.forEach((item) => {
    const quantity = toFiniteNumber(item.quantity) || 0;
    if (quantity > 0) {
      units.add(normalizeProductUnit(item.unit));
    }
  });

  return Array.from(units).sort();
}

export function hasMixedCargoQuantityUnits(cargoItems: SharedTransportCargoItem[]): boolean {
  return getCargoQuantityUnits(cargoItems).length > 1;
}

export function calculateOrderMetrics(order: Pick<OrderOptionDto, 'products'> | Pick<RouteOrderDto, 'order'>): SharedTransportOrderMetrics {
  const products = 'products' in order ? order.products : order.order.products;
  return calculateProductsMetrics(products || []);
}

export function calculateCargoItemsMetrics(cargoItems: SharedTransportCargoItem[]): SharedTransportOrderMetrics {
  return calculateProductsMetrics(cargoItems);
}

// Full-precision counterpart for allocation math (S14): the basis must use the
// unrounded measure the backend persists from, never the 3dp display value.
export function calculateCargoItemsMetricsRaw(cargoItems: SharedTransportCargoItem[]): SharedTransportOrderMetrics {
  return calculateProductsMetricsRaw(cargoItems);
}

// F1 (trailer weight indicator): display-only utilization vs a standard
// trailer (TRAILER_PAYLOAD_KG, single home in transportCostAllocation.ts).
// NEVER a save-blocking limit. The LDM indicator was removed with LDM
// (decision 2026-09-30).
export interface TrailerCapacityIndicator {
  ratio: number; // 0..Infinity, e.g. 0.55 = 55% of capacity used
  percent: number; // ratio * 100, rounded to 1dp for display
  overCapacity: boolean; // ratio > 1
  loaded: number;
  capacity: number;
}

// Weight indicator is intentionally ONLY shown when totalWeight > 0 — most
// cargo lines have no grossWeight entered today, so a 0.00 kg weight would
// otherwise render a permanently-empty, noisy "0 / 22800 kg" badge.
export function deriveWeightCapacityIndicator(totalWeight: number): TrailerCapacityIndicator | null {
  if (!(totalWeight > 0)) {
    return null;
  }
  const ratio = totalWeight / TRAILER_PAYLOAD_KG;
  return {
    ratio,
    percent: Number((ratio * 100).toFixed(1)),
    overCapacity: ratio > 1,
    loaded: totalWeight,
    capacity: TRAILER_PAYLOAD_KG,
  };
}

// Read-only per-line metrics for one dimensioned source at a given quantity.
// Area and volume come from the shared dimensionConversion helpers (the single
// home for the piece → m²/m³ formula). `null` when the source lacks the
// dimensions, so the UI can render a "fill in dimensions" state.
export interface CargoLineMetrics {
  areaSqm: number | null;
  volume: number | null;
}

export function calculateCargoLineMetrics(
  source: ConversionSource & { unit?: string | null },
  quantity: number,
): CargoLineMetrics {
  const areaSqm = calculateAreaM2(source, quantity);
  const volume = calculateVolumeM3(source, quantity);

  return {
    areaSqm: areaSqm !== null ? Number(areaSqm.toFixed(3)) : null,
    volume: volume !== null ? Number(volume.toFixed(3)) : null,
  };
}

// True when the line carries a positive quantity but no metric can be derived
// (missing dimensions) — the "—" / tooltip signal for a single cargo line.
export function cargoLineMetricsAreEmpty(metrics: CargoLineMetrics): boolean {
  return metrics.areaSqm === null && metrics.volume === null;
}

// True when any loaded item (quantity > 0) lacks the dimension fields needed to
// derive a volume, so the cargo totals understate reality. m³ bulk cargo
// carries its own volume and is never flagged.
export function hasMissingCargoDimensions(cargoItems: SharedTransportCargoItem[]): boolean {
  return cargoItems.some((item) => {
    const quantity = toFiniteNumber(item.quantity) || 0;
    if (quantity <= 0 || item.countInPiecesOnly === true) {
      return false;
    }
    return calculateProductVolumeCbm(item) <= 0;
  });
}

type ProductsMetricsInput = Array<{
  quantity?: number;
  quantityPcs?: number | null;
  unit?: string;
  grossWeight?: number;
  catalogGrossWeight?: number;
  length?: number;
  width?: number;
  height?: number;
  thickness?: number;
  countInPiecesOnly?: boolean;
}>;

// Full-precision totals, mirroring the backend twin's calculateTransportMetrics
// (transportMetrics.js) — UNROUNDED.
function computeProductsMetricsTotals(products: ProductsMetricsInput) {
  return (products || []).reduce(
    (accumulator, product) => {
      const quantity = toFiniteNumber(product.quantity) || 0;
      const unit = normalizeProductUnit(product.unit);
      const grossWeight = toFiniteNumber(product.grossWeight) || toFiniteNumber(product.catalogGrossWeight) || 0;
      const areaSqm = calculateProductAreaSqm(product);
      const volume = calculateProductVolumeCbm(product);

      accumulator.quantityByUnit[unit] = (accumulator.quantityByUnit[unit] || 0) + quantity;
      // Piece weight × pieces, never × m² / m³ (backend twin calculateProductPieces).
      const pieces = calculateProductPieces(product);
      if (grossWeight > 0 && pieces !== null) {
        accumulator.weight += grossWeight * pieces;
      } else if (quantity > 0) {
        accumulator.weightIncomplete = true;
      }
      accumulator.areaSqm += areaSqm;
      accumulator.volume += volume;
      // A product counted only in pieces lacks m² / m³ on purpose: reported
      // separately, never as missing dimensions (backend transportMetrics).
      if (quantity > 0 && product.countInPiecesOnly === true) {
        accumulator.countedInPiecesOnly = true;
      } else {
        if (quantity > 0 && !(areaSqm > 0)) {
          accumulator.areaIncomplete = true;
        }
        if (quantity > 0 && !(volume > 0)) {
          accumulator.volumeIncomplete = true;
        }
      }

      return accumulator;
    },
    {
      weight: 0,
      areaSqm: 0,
      volume: 0,
      areaIncomplete: false,
      volumeIncomplete: false,
      countedInPiecesOnly: false,
      weightIncomplete: false,
      quantityByUnit: {} as Record<string, number>,
    }
  );
}

function calculateProductsMetricsRaw(products: ProductsMetricsInput): SharedTransportOrderMetrics {
  return computeProductsMetricsTotals(products);
}

function calculateProductsMetrics(products: ProductsMetricsInput): SharedTransportOrderMetrics {
  const totals = computeProductsMetricsTotals(products);
  return {
    weight: Number(totals.weight.toFixed(2)),
    areaSqm: Number(totals.areaSqm.toFixed(3)),
    volume: Number(totals.volume.toFixed(3)),
    areaIncomplete: totals.areaIncomplete,
    volumeIncomplete: totals.volumeIncomplete,
    countedInPiecesOnly: totals.countedInPiecesOnly,
    weightIncomplete: totals.weightIncomplete,
    quantityByUnit: totals.quantityByUnit,
  };
}

function normalizeProductUnit(unit: unknown): string {
  return normalizeQuantityUnit(String(unit || 'pcs'));
}

/** Saved share of one order (edit mode) shown when the screen cannot compute it. */
export interface SavedOrderShare {
  percent: number | null;
  cost: number | null;
}

export interface SharedTransportAllocationPreview {
  rows: SharedTransportPreviewRow[];
  // Display numbers of orders whose split basis is a partial sum because ≥1
  // loaded line has no m² / m³ (mirrors the backend MISSING_DIMENSIONS
  // allocation warning). Empty when nothing is missing.
  missingDimensionOrders: string[];
  /** szt. key over different units: the split weighs pieces (MIXED_UNITS). */
  mixedUnits: boolean;
  /**
   * Goods value key: the split is computed by the server only (OPERATIONS
   * has no prices). Rows carry the saved shares when known, otherwise null.
   */
  valueComputedOnSave: boolean;
}

type PreviewPosition = AllocationKeyPosition & { pcs?: number | null };

const DEFAULT_KEY_BASIS_METRICS: ReadonlyArray<{
  metric: 'volume' | 'areaSqm' | 'pieces' | 'weight';
  incompleteFlag: 'volumeIncomplete' | 'areaIncomplete' | null;
}> = [
  { metric: 'volume', incompleteFlag: 'volumeIncomplete' },
  { metric: 'areaSqm', incompleteFlag: 'areaIncomplete' },
  { metric: 'pieces', incompleteFlag: null },
  { metric: 'weight', incompleteFlag: null },
];

function defaultKeyMetricValue(
  metrics: SharedTransportOrderMetrics,
  metric: (typeof DEFAULT_KEY_BASIS_METRICS)[number]['metric'],
): number {
  if (metric === 'pieces') return Number(metrics.quantityByUnit.pcs) || 0;
  return Number(metrics[metric]) || 0;
}

function toPreviewPosition(item: SharedTransportCargoItem | OrderOptionDto['products'][number]): PreviewPosition {
  const record = item as SharedTransportCargoItem & { name?: string };
  return {
    productName: record.productName || record.name || null,
    unit: record.unit,
    quantity: record.quantity,
    pcs: record.quantityPcs ?? null,
    m2: record.quantityM2 ?? null,
    m3: record.quantityM3 ?? null,
    width: record.width,
    height: record.height,
    length: record.length,
    thickness: record.thickness,
    countInPiecesOnly: record.countInPiecesOnly === true,
    source: 'ZK',
  };
}

/**
 * Between-loads preview of a truck's split over its customer orders — mirror
 * of backend transportService.buildAllocationDraftsFromLoads for the customer
 * side (the form shows it for an OUTBOUND truck with ≥2 orders). One key per
 * truck: szt. → quantity (pieces when the units differ), m² / m³ → entered or
 * derived metric, ręcznie → sum of the manual line amounts, wartość towaru →
 * computed by the server on save (saved shares in edit mode).
 */
export function buildAllocationPreview(
  orders: OrderOptionDto[],
  transportRate: number | null | undefined,
  costAllocationMethod: TransportCostAllocationMethod = DEFAULT_ALLOCATION_METHOD,
  cargoItems: SharedTransportCargoItem[] = [],
  savedSharesByOrderId: Record<string, SavedOrderShare> = {},
): SharedTransportAllocationPreview {
  const betweenLoads = BETWEEN_LOADS_METHOD[costAllocationMethod] || 'QUANTITY';
  const cargoItemsByOrderId = cargoItems.reduce<Record<string, SharedTransportCargoItem[]>>((accumulator, item) => {
    if (!accumulator[item.orderId]) {
      accumulator[item.orderId] = [];
    }

    accumulator[item.orderId].push(item);
    return accumulator;
  }, {});
  const loads = orders.map((order) => {
    const cargoItemsForOrder = cargoItemsByOrderId[order.id] || [];
    const sourceItems = cargoItemsForOrder.length ? cargoItemsForOrder : (order.products || []);
    const positions = (sourceItems as Array<SharedTransportCargoItem | OrderOptionDto['products'][number]>)
      .map(toPreviewPosition)
      .filter((position) => Number(position.quantity) > 0);
    return {
      order,
      cargoItemsForOrder,
      positions,
      // Display only (3 dp) — the basis below uses the raw positions.
      metrics: cargoItemsForOrder.length
        ? calculateCargoItemsMetrics(cargoItemsForOrder)
        : calculateOrderMetrics(order),
    };
  });

  if (betweenLoads === 'VALUE') {
    return {
      rows: loads.map(({ order, metrics }) => ({
        orderId: order.id,
        displayOrderNumber: order.displayOrderNumber || order.orderNumber,
        customerName: order.customerName,
        basisValue: null,
        allocationPercent: savedSharesByOrderId[order.id]?.percent ?? null,
        allocatedTransportCost: savedSharesByOrderId[order.id]?.cost ?? null,
        metrics,
      })),
      missingDimensionOrders: [],
      mixedUnits: false,
      valueComputedOnSave: true,
    };
  }

  // Default key (szt.) BETWEEN the loads = backend pickDefaultKeyBasisMetric
  // (decision 2026-10-01, as v3.8.1 without LDM): one metric for the whole
  // truck — the first of m³, m², pieces (Σ quantity in szt.), weight that is
  // > 0 for EVERY load; none → every load weighs the same.
  const mixedUnits = betweenLoads === 'QUANTITY' && hasMixedUnits(loads.flatMap((load) => load.positions));
  const rawMetrics = loads.map((load) => calculateCargoItemsMetricsRaw(
    (load.cargoItemsForOrder.length ? load.cargoItemsForOrder : (load.order.products || [])) as SharedTransportCargoItem[],
  ));
  const defaultBasis = betweenLoads === 'QUANTITY'
    ? DEFAULT_KEY_BASIS_METRICS.find(({ metric }) => (
      rawMetrics.length > 0 && rawMetrics.every((metrics) => defaultKeyMetricValue(metrics, metric) > 0)
    )) || null
    : null;
  const withBasis = loads.map((load, index) => {
    if (betweenLoads === 'MANUAL') {
      const basis = load.cargoItemsForOrder.reduce((sum, item) => {
        const amount = Number(item.manualAllocatedTransportCost);
        return sum + (Number.isFinite(amount) && amount > 0 ? amount : 0);
      }, 0);
      return { ...load, basisValue: basis, basisIncomplete: false };
    }
    if (betweenLoads === 'SQM' || betweenLoads === 'CBM') {
      // „Liczymy tylko w sztukach”: such positions weigh 0 with their own warning.
      const counted = load.positions.filter((position) => position.countInPiecesOnly !== true);
      const metrics = counted.map((position) => (
        betweenLoads === 'SQM' ? positionArea(position) : positionVolume(position)
      ));
      return {
        ...load,
        basisValue: metrics.reduce((sum, metric) => sum + metric, 0),
        basisIncomplete: metrics.some((metric) => !(metric > 0)),
      };
    }
    const metrics = rawMetrics[index];
    return {
      ...load,
      basisValue: defaultBasis ? defaultKeyMetricValue(metrics, defaultBasis.metric) : 0,
      basisIncomplete: Boolean(defaultBasis?.incompleteFlag && metrics[defaultBasis.incompleteFlag]),
    };
  });

  const totalBasis = withBasis.reduce((sum, row) => sum + row.basisValue, 0);
  const totalRate = Number(transportRate || 0);
  // Money follows the raw basis (backend R1.m2); the percent is display only.
  const weights = withBasis.map((row) => (totalBasis > 0 ? row.basisValue : 1));
  const allocatedCosts = allocateProportionally(totalRate, weights);
  const equalShare = 100 / Math.max(withBasis.length, 1);

  const rows = withBasis.map((row, index) => ({
    orderId: row.order.id,
    displayOrderNumber: row.order.displayOrderNumber || row.order.orderNumber,
    customerName: row.order.customerName,
    basisValue: Number(row.basisValue.toFixed(3)),
    allocationPercent: totalBasis > 0
      ? Number(((row.basisValue / totalBasis) * 100).toFixed(4))
      : Number(equalShare.toFixed(4)),
    allocatedTransportCost: allocatedCosts[index] || 0,
    metrics: row.metrics,
  }));

  return {
    rows,
    missingDimensionOrders: withBasis
      .filter((row) => row.basisIncomplete)
      .map((row) => row.order.displayOrderNumber || row.order.orderNumber),
    mixedUnits,
    valueComputedOnSave: false,
  };
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

// HIGH (Enter submit): pressing Enter in a single-line input anywhere in the
// transport form (e.g. "Firma transportowa", the split-count InputNumber, a
// per-truck rate InputNumber) bubbles up to antd's <Form onFinish> and CREATES
// the transport mid-configuration. The rule now lives in utils/formEnterGuard
// because the WZ and PZ create forms hit the identical bug class; re-exported
// here so this module stays the import site SharedTransportForm already uses.
export { shouldPreventEnterFormSubmit } from '../utils/formEnterGuard';

/**
 * Is the goods value of this truck known on the form (sticky summary
 * „Wartość towaru” / „Łączny koszt”)? It is operator input on the customer
 * cargo lines. Not known — shown "—", never „0,00” — when the truck carries ZD
 * goods (their value is not on this form), when no cargo line is loaded, or
 * when a saved line never had a value and the operator has not typed one
 * (the field is seeded with 0). Display only; the payload is unchanged.
 */
export function isTransportGoodsValueKnown({
  supplierOrderCount,
  rows,
}: {
  supplierOrderCount: number;
  rows: Array<{ goodsValueTotal?: number | null; saved: boolean; savedGoodsValueTotal?: number | null; touched: boolean }>;
}): boolean {
  if (supplierOrderCount > 0 || rows.length === 0) return false;
  return rows.every((row) => {
    if (row.saved && row.savedGoodsValueTotal == null && !row.touched) return false;
    return row.goodsValueTotal !== null && row.goodsValueTotal !== undefined;
  });
}
